/**
 * src/lib/hooks/useCRMData.js — CRM & Vessel domain hook.
 *
 * Owns: crm, vessels, vesselContacts, clients state,
 *       all CRM/vessel mutation callbacks.
 *
 * Extracted from useAppData.js (Phase 81-01).
 */

import { useState, useCallback } from 'react';
import { fetchCRM, fetchVessels, fetchAllVesselContacts, fetchClients, updateCRMStatus, updateCRMDeal, updateVessel, createVessel, createVesselContact, createCRMDeal, createCRMActivity, updateCRMActivity, updateVesselContact, deleteVesselContact } from '../api/crm';
import { logAction, logResult, logMergeDecision } from '../observability';
import { logAppError } from '../errorReporter';
import { mergeWithOptimistic } from '../mergeWithOptimistic';
import { reconcileCollection } from '../offline/entityCache';
import { getOnline } from '../offline/networkStatus';
import { maybeEnqueueOffline } from '../offline/enqueueOffline';
import { offlineCreateGate } from './crmOfflineCreate';

export function useCRMData({ scheduleReload, showError, stages, offlineEnabled = false }) {
  const [crm, setCrm] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [vesselContacts, setVesselContacts] = useState([]);
  const [clients, setClients] = useState([]);

  const handleUpdateDealStatus = useCallback(async (dealId, newStatus, newStageRow) => {
    const cid = logAction('crm:updateDealStatus', { dealId, newStatus });
    const targetStage = newStageRow || stages.find(s => s.id === newStatus);
    let previousDeal = null;
    setCrm(prev => prev.map(d => {
      if (d.id !== dealId) return d;
      previousDeal = d;
      return {
        ...d,
        status: newStatus,
        ...(targetStage ? { stage_id: targetStage.id, stages: targetStage } : {}),
      };
    }));

    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'crm', type: 'update_status', entity_id: dealId, payload: { status: newStatus } });
      if (q.queued) {
        setCrm(prev => prev.map(d => d.id === dealId ? { ...d, _pendingSync: true } : d));
        logResult('crm:updateDealStatus', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateCRMStatus(dealId, newStatus);
      if (result?.success) {
        logResult('crm:updateDealStatus', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateDealStatus');
      } else if (previousDeal) {
        setCrm(prev => prev.map(d => d.id === dealId ? previousDeal : d));
        logResult('crm:updateDealStatus', 'rollback', { correlationId: cid, error: result?.error });
        showError(result?.error || 'Failed to update deal status');
      }
      return result;
    } catch (e) {
      if (previousDeal) {
        setCrm(prev => prev.map(d => d.id === dealId ? previousDeal : d));
      }
      logAppError(e, 'useCRMData:updateDealStatus');
      logResult('crm:updateDealStatus', 'error', { correlationId: cid, error: e.message });
      showError(e.message || 'Failed to update deal status');
      return { success: false, error: e.message };
    }
  }, [scheduleReload, showError, stages, offlineEnabled]);

  const handleUpdateDeal = useCallback(async (dealId, updates) => {
    const cid = logAction('crm:updateDeal', { dealId, fields: Object.keys(updates) });
    let previousDeal = null;
    setCrm(prev => prev.map(d => {
      if (d.id !== dealId) return d;
      previousDeal = d;
      return { ...d, ...updates };
    }));

    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'crm', type: 'update_deal', entity_id: dealId, payload: updates });
      if (q.queued) {
        setCrm(prev => prev.map(d => d.id === dealId ? { ...d, _pendingSync: true } : d));
        logResult('crm:updateDeal', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateCRMDeal(dealId, updates);
      if (result?.success) {
        logResult('crm:updateDeal', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateDeal');
      } else if (previousDeal) {
        setCrm(prev => prev.map(d => d.id === dealId ? previousDeal : d));
        logResult('crm:updateDeal', 'rollback', { correlationId: cid, error: result?.error });
        showError(result?.error || 'Failed to update deal');
      }
      return result;
    } catch (e) {
      if (previousDeal) {
        setCrm(prev => prev.map(d => d.id === dealId ? previousDeal : d));
      }
      logAppError(e, 'useCRMData:updateDeal');
      logResult('crm:updateDeal', 'error', { correlationId: cid, error: e.message });
      showError(e.message || 'Failed to update deal');
      return { success: false, error: e.message };
    }
  }, [scheduleReload, showError, offlineEnabled]);

  const handleUpdateVessel = useCallback(async (vesselId, updates) => {
    const cid = logAction('crm:updateVessel', { vesselId });
    let previousVessel = null;
    setVessels(prev => prev.map(v => {
      if (v.id !== vesselId) return v;
      previousVessel = v;
      return { ...v, ...updates };
    }));

    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'crm', type: 'update_vessel', entity_id: vesselId, payload: updates });
      if (q.queued) {
        setVessels(prev => prev.map(v => v.id === vesselId ? { ...v, _pendingSync: true } : v));
        logResult('crm:updateVessel', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateVessel(vesselId, updates);
      if (result?.success) {
        logResult('crm:updateVessel', 'success', { correlationId: cid });
        scheduleReload(2000, 'updateVessel');
      } else if (previousVessel) {
        setVessels(prev => prev.map(v => v.id === vesselId ? previousVessel : v));
        logResult('crm:updateVessel', 'rollback', { correlationId: cid });
        showError(result?.error || 'Failed to update vessel');
      }
      return result;
    } catch (e) {
      if (previousVessel) {
        setVessels(prev => prev.map(v => v.id === vesselId ? previousVessel : v));
      }
      logAppError(e, 'useCRMData:updateVessel');
      logResult('crm:updateVessel', 'error', { correlationId: cid, error: e.message });
      showError(e.message || 'Failed to update vessel');
      return { success: false, error: e.message };
    }
  }, [scheduleReload, showError, offlineEnabled]);

  const handleCreateVessel = useCallback(async (data) => {
    const cid = logAction('crm:createVessel', { vessel_name: data.vessel_name });

    const gate = await offlineCreateGate({
      offlineEnabled, type: 'create_vessel', data, setState: setVessels,
      buildOptimistic: () => ({ ...data, archived_at: null }),
    });
    if (gate.queued) {
      logResult('crm:createVessel', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: gate.record };
    }

    try {
      const result = await createVessel(data);
      if (result?.success) {
        logResult('crm:createVessel', 'success', { correlationId: cid });
        scheduleReload(500, 'createVessel');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:createVessel');
      logResult('crm:createVessel', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // Offline CREATE for a CRM deal. (Online deal-create lives in PipelineView via
  // createCRMDeal; this gate gives that flow an offline home + optimistic row.
  // PipelineView can route through this when offline — see contract notes.)
  const handleCreateDeal = useCallback(async (data) => {
    const cid = logAction('crm:createDeal', { opportunity: data.opportunity });

    const gate = await offlineCreateGate({
      offlineEnabled, type: 'create_deal', data, setState: setCrm,
      buildOptimistic: () => ({ ...data, status: data.status || null }),
    });
    if (gate.queued) {
      logResult('crm:createDeal', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: gate.record };
    }

    try {
      const result = await createCRMDeal(data);
      if (result?.success) {
        logResult('crm:createDeal', 'success', { correlationId: cid });
        // TICKET-1122: show the new deal NOW from the server's inserted row; the
        // scheduled reload then backfills the joins (stage, vessel, type labels).
        const row = result.deal;
        if (row?.id) setCrm(prev => [row, ...prev.filter(d => d.id !== row.id)]);
        scheduleReload(500, 'createDeal');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:createDeal');
      logResult('crm:createDeal', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleCreateVesselContact = useCallback(async (payload) => {
    const cid = logAction('crm:createVesselContact', {});

    const gate = await offlineCreateGate({
      offlineEnabled, type: 'create_contact', data: payload, setState: setVesselContacts,
      buildOptimistic: () => ({
        ...payload,
        contact_name: payload.contact_name || [payload.first_name, payload.last_name].filter(Boolean).join(' ').trim() || null,
        role: payload.role || (Array.isArray(payload.roles) && payload.roles.length ? payload.roles[0] : null),
      }),
    });
    if (gate.queued) {
      logResult('crm:createVesselContact', 'queued', { correlationId: cid });
      return { success: true, queued: true, record: gate.record };
    }

    try {
      const result = await createVesselContact(payload);
      if (result?.success) {
        logResult('crm:createVesselContact', 'success', { correlationId: cid });
        scheduleReload(500, 'createVesselContact');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:createVesselContact');
      logResult('crm:createVesselContact', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // ─── Notes / timeline (crm_activities) — offline create + edit ─────────────
  // The deal History timeline is crm_activities. These gates let an operator add
  // or edit a note while offline; the op replays with the client id (create) /
  // record id (update) so it's idempotent. State for the timeline lives in the
  // consuming component (DealHistoryTab), so these return the optimistic record
  // for the caller to splice in — no shared array setter here.
  const handleCreateActivity = useCallback(async (payload) => {
    const cid = logAction('crm:createActivity', { deal_id: payload.deal_id || payload.crm_id });

    {
      const q = await maybeEnqueueOffline({
        offlineEnabled, domain: 'crm', type: 'create_activity',
        entity_id: payload.id, payload,
      });
      if (q.queued) {
        logResult('crm:createActivity', 'queued', { correlationId: cid });
        return { success: true, queued: true, record: { ...payload, _pendingSync: true } };
      }
    }

    try {
      const result = await createCRMActivity(
        payload.crm_id || payload.deal_id,
        payload.type,
        payload.details,
        payload.interaction_type || payload.purpose || null,
        payload.planned_date || null,
        payload.contact_id || null,
        payload.is_decision,
        payload.id,
      );
      if (result?.success) {
        logResult('crm:createActivity', 'success', { correlationId: cid });
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:createActivity');
      logResult('crm:createActivity', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [offlineEnabled]);

  const handleUpdateActivity = useCallback(async (activityId, updates) => {
    const cid = logAction('crm:updateActivity', { activityId, fields: Object.keys(updates || {}) });

    {
      const q = await maybeEnqueueOffline({
        offlineEnabled, domain: 'crm', type: 'update_activity',
        entity_id: activityId, payload: updates,
      });
      if (q.queued) {
        logResult('crm:updateActivity', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateCRMActivity(activityId, updates);
      if (result?.success) {
        logResult('crm:updateActivity', 'success', { correlationId: cid });
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:updateActivity');
      logResult('crm:updateActivity', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [offlineEnabled]);

  const handleUpdateVesselContact = useCallback(async (contactId, data) => {
    const cid = logAction('crm:updateVesselContact', { contactId });
    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'crm', type: 'update_contact', entity_id: contactId, payload: data });
      if (q.queued) {
        setVesselContacts(prev => prev.map(c => c.id === contactId ? { ...c, ...data, _pendingSync: true } : c));
        logResult('crm:updateVesselContact', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await updateVesselContact(contactId, data);
      if (result?.success) {
        logResult('crm:updateVesselContact', 'success', { correlationId: cid });
        scheduleReload(500, 'updateVesselContact');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:updateVesselContact');
      logResult('crm:updateVesselContact', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  const handleDeleteVesselContact = useCallback(async (contactId) => {
    const cid = logAction('crm:deleteVesselContact', { contactId });
    {
      const q = await maybeEnqueueOffline({ offlineEnabled, domain: 'crm', type: 'delete_contact', entity_id: contactId, payload: {} });
      if (q.queued) {
        setVesselContacts(prev => prev.filter(c => c.id !== contactId));
        logResult('crm:deleteVesselContact', 'queued', { correlationId: cid });
        return { success: true, queued: true };
      }
    }

    try {
      const result = await deleteVesselContact(contactId);
      if (result?.success) {
        logResult('crm:deleteVesselContact', 'success', { correlationId: cid });
        // Optimistic local removal so the row disappears immediately;
        // scheduleReload re-syncs from server.
        setVesselContacts(prev => prev.filter(c => c.id !== contactId));
        scheduleReload(500, 'deleteVesselContact');
      }
      return result;
    } catch (e) {
      logAppError(e, 'useCRMData:deleteVesselContact');
      logResult('crm:deleteVesselContact', 'error', { correlationId: cid, error: e.message });
      return { success: false, error: e.message };
    }
  }, [scheduleReload, offlineEnabled]);

  // ─── Fetch / Apply / Merge (Phase 81-02) ───────────────────
  const fetchAll = useCallback(async () => {
    const [crm, vessels, vesselContacts, clients] = await Promise.all([
      fetchCRM().catch(e => { console.warn('[CRMData] CRM failed:', e.message); return []; }),
      fetchVessels().catch(e => { console.warn('[CRMData] Vessels failed:', e.message); return []; }),
      fetchAllVesselContacts().catch(e => { console.warn('[CRMData] VesselContacts failed:', e.message); return []; }),
      fetchClients().catch(e => { console.warn('[CRMData] Clients failed:', e.message); return []; }),
    ]);
    if (offlineEnabled) {
      const online = getOnline();
      const [c, v, vc, cl] = await Promise.all([
        reconcileCollection('crm', crm, online),
        reconcileCollection('vessels', vessels, online),
        reconcileCollection('vesselContacts', vesselContacts, online),
        reconcileCollection('clients', clients, online),
      ]);
      return { crm: c, vessels: v, vesselContacts: vc, clients: cl };
    }
    return { crm, vessels, vesselContacts, clients };
  }, [offlineEnabled]);

  const applyFetchResult = useCallback((data) => {
    setCrm(Array.isArray(data.crm) ? data.crm : []);
    setVessels(Array.isArray(data.vessels) ? data.vessels : []);
    setVesselContacts(Array.isArray(data.vesselContacts) ? data.vesselContacts : []);
    setClients(Array.isArray(data.clients) ? data.clients : []);
  }, []);

  const mergeAll = useCallback((data, optimisticTimestamps, now) => {
    const merge = (setter, newData) => {
      if (!newData) return;
      setter(prev => {
        const { merged, anyChanged, expiredIds } = mergeWithOptimistic(prev, newData, optimisticTimestamps, now);
        for (const { id, age_ms } of expiredIds) logMergeDecision(id, 'ttl_expired', { age_ms });
        return anyChanged ? merged : prev;
      });
    };
    merge(setCrm, data.crm);
    merge(setVessels, data.vessels);
    merge(setVesselContacts, data.vesselContacts);
    merge(setClients, data.clients);
  }, []);

  return {
    crm, setCrm,
    vessels, setVessels,
    vesselContacts, setVesselContacts,
    clients, setClients,
    handleUpdateDealStatus,
    handleUpdateDeal,
    handleUpdateVessel,
    handleCreateVessel,
    handleCreateDeal,
    handleCreateVesselContact,
    handleUpdateVesselContact,
    handleDeleteVesselContact,
    handleCreateActivity,
    handleUpdateActivity,
    fetchAll,
    applyFetchResult,
    mergeAll,
  };
}
